/**
 * Duplicate a template (REQ-8).
 *
 * A duplicate is just a DRAFT: the copy is handed to the unified Template Editor
 * with **no id**, so the editor opens in create mode and nothing exists on the
 * server until the user saves. Cancel therefore leaves the account untouched,
 * and the original template is never modified.
 *
 * Row ids are dropped rather than reused — the editor re-keys draft rows anyway,
 * and carrying the source ids would make two templates claim the same subitem
 * ids for no benefit.
 */
import { duplicateContent } from './groupContent';

/**
 * Default name for a copy: "{name} copy", then "{name} copy 2", "{name} copy 3"…
 * if that name is already taken. Comparison is trimmed + case-insensitive, so
 * "Item Prod copy" and "item prod COPY " count as the same name.
 *
 * @param {string} name           name of the template being copied
 * @param {string[]} existingNames all template names in the account
 * @returns {string}
 */
export function copyName(name, existingNames = []) {
  // .trim() on the whole thing so an empty source name gives "copy", not " copy"
  const base = `${String(name == null ? '' : name).trim()} copy`.trim();
  const taken = new Set(
    existingNames.map((n) => String(n == null ? '' : n).trim().toLowerCase()),
  );
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/**
 * Build the editor draft for a copy of `template`.
 * The absent `id` is what makes the editor save it as a new template.
 *
 * @param {object} template        the stored template being copied
 * @param {string[]} existingNames all template names in the account
 */
export function duplicateDraft(template, existingNames = []) {
  return {
    // no id — this is a new template
    name: copyName(template.name, existingNames),
    access_level: template.access_level,
    // a group template carries its body in `content`; nothing is shared with the source
    type: template.type === 'group' ? 'group' : 'subitem',
    content: template.type === 'group' ? duplicateContent(template.content) : null,
    columns: (template.columns || []).map((c) => ({ ...c })),
    subitems: (template.subitems || []).map((s) => ({
      name: s.name,
      column_values: (s.column_values || []).map((v) => ({ ...v })),
      // updates travel with the row: body and mentions, copied, never shared
      updates: (s.updates || []).map((u) => ({
        body: u.body,
        mentions: (u.mentions || []).map((m) => ({ ...m })),
      })),
    })),
  };
}
